import cn from 'classnames';
import { useState, useRef, useEffect, Fragment, MouseEvent, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { IconMenu } from 'UIPackage/MainModule/components/IconMenu';

import { MagicLine } from './MagicLine';
import {
  ROUTE_FEEDBACK,
  ROUTE_KNM,
  ROUTE_NEWS,
  ROUTE_OPEN_DATA,
  ROUTE_OPEN_DATA_CHECK,
  ROUTE_PORTAL,
  ROUTE_SEARCH,
  ROUTE_SYSTEM_INOPERABILITY,
} from '../constants/routes';
import { SCREEN_LARGE_DESKTOP } from '../constants/screen';
import styles from './NavBar.module.scss';
import { getCoreScreenSize } from '../selectors/coreSelectors';
import { IRootState } from '../interfaces/IRootState';
import { feedbackReset } from '../../../FeedbackPackage/MainModule/actions/feedbackActions';

interface IRoute {
  to: string;
  title: string;
  items?: IRoute[];
}

export const navLinks: IRoute[] = [
  {
    to: ROUTE_PORTAL,
    title: 'Главная страница',
  },
  {
    to: ROUTE_OPEN_DATA,
    title: 'Открытые данные',
  },
  {
    to: ROUTE_SEARCH,
    title: 'Поиск проверок',
  },
  {
    to: ROUTE_NEWS,
    title: 'Новости',
    items: [
      {
        to: ROUTE_NEWS,
        title: 'Новостная лента',
      },
      {
        to: ROUTE_SYSTEM_INOPERABILITY,
        title: 'Сведения о неработоспособности',
      },
    ],
  },
  {
    to: ROUTE_FEEDBACK,
    title: 'Обратная связь',
  },
];

interface INavBarStoreProps {
  // enumScreenSize
  screenSize: string;
}

const NavBarWithoutRouter = ({ screenSize }: INavBarStoreProps) => {
  const activeRef = useRef<HTMLAnchorElement>(null);
  const [activeLeft, setActiveLeft] = useState(0);
  const [activeWidth, setActiveWidth] = useState(0);
  const [targetLeft, setTargetLeft] = useState(0);
  const [targetWidth, setTargetWidth] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();

  const feedbackRoute = useCallback(() => {
    dispatch(feedbackReset());
    history.push(ROUTE_FEEDBACK);
  }, [dispatch, history]);

  useEffect(() => {
    if (activeRef.current) {
      setActiveLeft(activeRef.current.offsetLeft);
      setActiveWidth(activeRef.current.offsetWidth);
      setTargetLeft(activeRef.current.offsetLeft);
      setTargetWidth(activeRef.current.offsetWidth);
    }
  }, []);

  const onMouseEnter = (e: MouseEvent<HTMLElement>) => {
    setTargetLeft(e.currentTarget.offsetLeft);
    setTargetWidth(e.currentTarget.offsetWidth);
  };

  const onMouseLeave = () => {
    setTargetLeft(activeLeft);
    setTargetWidth(activeWidth);
  };

  const isActive = (path: string) =>
    window.location.pathname === path ||
    (path === ROUTE_OPEN_DATA && window.location.pathname.includes(ROUTE_OPEN_DATA_CHECK)) ||
    (path === ROUTE_SEARCH && window.location.pathname.includes(ROUTE_KNM)) ||
    (path === ROUTE_NEWS && window.location.pathname.includes(ROUTE_SYSTEM_INOPERABILITY));

  const onMenuButtonClick = () => setMenuOpen(!menuOpen);

  if (screenSize !== SCREEN_LARGE_DESKTOP) {
    return (
      <nav className={styles.MenuContainerMobile}>
        {navLinks
          .filter(link => isActive(link.to))
          .map(link => (
            <Fragment key={link.title}>
              <div key={link.title} className={cn(styles.menuItem, styles.menuItemActive)}>
                {link.title}
              </div>
              <button type="button" onClick={onMenuButtonClick}>
                <IconMenu />
              </button>
            </Fragment>
          ))}
        {menuOpen && (
          <div className={styles.menuListMobile}>
            {navLinks.map(link => (
              <Link
                ref={isActive(link.to) ? activeRef : null}
                key={link.title}
                to={link.to}
                id={link.to}
                data-active={isActive(link.to)}
                className={cn(styles.menuItem, styles.menuItemMobile, {
                  [styles.menuItemActive]: isActive(link.to),
                })}
              >
                {link.title}
              </Link>
            ))}
          </div>
        )}
      </nav>
    );
  }

  return (
    <nav className={styles.MenuContainer}>
      {navLinks.map(link => (
        <div className={styles.NavItem} key={link.title}>
          <Link
            ref={isActive(link.to) ? activeRef : null}
            key={link.title}
            to={link.to}
            id={link.to}
            data-active={isActive(link.to)}
            className={cn(styles.menuItem, { [styles.menuItemActive]: isActive(link.to) })}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={link.to === ROUTE_FEEDBACK ? feedbackRoute : undefined}
          >
            {link.title}
          </Link>

          {link.items && (
            <div className={styles.NavSubitems}>
              {link.items.map(sublink => {
                const subLinkAttributes = {
                  ...(isActive(sublink.to) && { 'data-active': true }),
                };

                return (
                  <div className={styles.NavSubitem} key={sublink.to}>
                    <Link className={styles.NavSublink} {...subLinkAttributes} to={sublink.to}>
                      {sublink.title}
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
      {targetWidth !== 0 && <MagicLine targetLeft={targetLeft} targetWidth={targetWidth} />}
    </nav>
  );
};

const mapStateToProps = (state: IRootState) => ({
  screenSize: getCoreScreenSize(state),
});

export const NavBar = connect(mapStateToProps)(NavBarWithoutRouter);
